import { useEffect } from 'react';

import { AlertTriangleIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router';
import { useTitle } from 'react-use';

import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import useSegments from '@/segment/useSegments';
import { PageName } from '@/shared/types/segment';

const Unauthorized: React.FC = () => {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const error = searchParams.get('error');
  const description = searchParams.get('description');
  const requestId = searchParams.get('request_id');

  useTitle(t('pages.unauthorized'));

  useSegments(PageName.UNAUTHORIZED);

  const handleTryAgain = () => {
    window.location.href = '/';
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-subtle px-4 py-12 sm:px-6 lg:px-8">
      <div className="w-full max-w-md space-y-8">
        <Card className="border-destructive/50 bg-destructive/5">
          <CardHeader className="text-center">
            <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-destructive/10">
              <AlertTriangleIcon className="h-6 w-6 text-destructive" />
            </div>
            <CardTitle className="text-2xl font-bold text-destructive">
              {t('pages_text.unauthorized_title')}
            </CardTitle>
            <CardDescription className="mt-2 text-base">
              {error || t('errors.you_are_not_authorized')}
            </CardDescription>
          </CardHeader>

          <CardContent className="space-y-4">
            {description && (
              <Alert variant="destructive" className="border-destructive/50 bg-destructive/10">
                <AlertTriangleIcon className="h-4 w-4" />
                <AlertTitle>{t('pages_text.details')}</AlertTitle>
                <AlertDescription>{description}</AlertDescription>
              </Alert>
            )}

            <div className="text-center text-sm text-muted-foreground">
              <p>{t('pages_text.unauthorized_help')}</p>
            </div>

            <Button onClick={handleTryAgain} className="w-full">
              {t('common.try_again')}
            </Button>

            {requestId && (
              <div className="text-center text-xs text-muted-foreground">
                <p>
                  {t('auth.request_id')}: {requestId}
                </p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default Unauthorized;
